import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Space, Typography, Form, Input, Select, Button, Tag, App, Checkbox, Alert } from 'antd';
import { trpc } from '../../../lib/api';
import { clientTypeOptions } from '../../../lib/clientType';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { InvitePartnerUserModal } from './InvitePartnerUserModal';
import { useBreadcrumbTitle } from '../../../hooks/useBreadcrumb';

const { Title, Text } = Typography;

const statusOptions = [
  { label: '有効', value: 'active' },
  { label: '停止', value: 'inactive' },
];

const billingModelOptions = [
  { label: 'プラットフォーム課金', value: 'platform_billing' },
  { label: '代理店課金', value: 'partner_billing' },
];

const PARTNER_SCOPE_OPTIONS = [
  { value: 'companies:read', label: '会社情報の閲覧' },
  { value: 'locations:read', label: 'エリアの閲覧' },
  { value: 'locations:write', label: 'エリアの更新' },
  { value: 'equipments:read', label: '設備の閲覧' },
  { value: 'equipments:write', label: '設備の更新' },
  { value: 'documents:read', label: '書類の閲覧' },
  { value: 'documents:write', label: '書類の更新' },
  { value: 'files:upload', label: '書類のアップロード' },
  { value: 'files:share', label: 'ファイル共有' },
  { value: 'reports:read', label: '報告書の閲覧' },
  { value: 'reports:write', label: '報告書の作成・提出' },
  { value: 'users:manage', label: '外部ユーザー管理' },
] as const;

const RESOURCE_ALIASES: Record<string, string> = {
  location: 'locations',
  locations: 'locations',
  area: 'locations',
  areas: 'locations',
  equipment: 'equipments',
  equipments: 'equipments',
  document: 'documents',
  documents: 'documents',
};

const MODULE_MAP: Record<string, string> = {
  'locations:read': 'maintenance',
  'locations:write': 'maintenance',
  'equipments:read': 'maintenance',
  'equipments:write': 'maintenance',
  'documents:read': 'reports',
  'documents:write': 'reports',
};

type PartnerScopePayload = Record<string, unknown> | null | undefined;

const normalizeResourceKey = (resource: string) => {
  return RESOURCE_ALIASES[resource] ?? resource;
};

const getStringArrayProperty = (scopes: Record<string, unknown>, key: string): string[] => {
  const value = scopes[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
};

const normalizeScopeRoot = (scopes: Record<string, unknown>) => {
  if (scopes.permissions && typeof scopes.permissions === 'object') {
    return scopes.permissions as Record<string, unknown>;
  }
  return scopes;
};

const hasResourceArrayScope = (resources: string[], required: string) => {
  const [resource, action] = required.split(':');
  const normalizedResource = normalizeResourceKey(resource);
  const candidates = [
    required,
    `${normalizedResource}:${action}`,
    action === 'read' ? `${normalizedResource}:write` : null,
    `${normalizedResource}:*`,
    `${normalizedResource}:all`,
    String(normalizedResource),
    `${resource}:${action}`,
    action === 'read' ? `${resource}:write` : null,
    `${resource}:*`,
    resource,
  ].filter(Boolean) as string[];
  return resources.some((entry) => entry === '*' || candidates.includes(entry));
};

const hasObjectScope = (
  scopesRoot: Record<string, unknown>,
  required: string
) => {
  const [resource, action] = required.split(':');
  const normalizedResource = normalizeResourceKey(resource);
  const rawEntry = scopesRoot[normalizedResource] ?? scopesRoot[resource];

  if (rawEntry === true) {
    return true;
  }

  if (rawEntry && typeof rawEntry === 'object') {
    const entry = rawEntry as Record<string, unknown>;
    if (entry[action] === true) {
      return true;
    }
    if (action === 'read' && entry.view === true) {
      return true;
    }
    if (action === 'write' && entry.edit === true) {
      return true;
    }
    if (action === 'read' && entry.write === true) {
      return true;
    }
    if (action === 'read' && entry.edit === true) {
      return true;
    }
  }

  return false;
};

const hasPartnerScope = (
  scopes: PartnerScopePayload,
  required: string
) => {
  if (!scopes || typeof scopes !== 'object') {
    return false;
  }

  const scopeRecord = scopes as Record<string, unknown>;
  const resources = getStringArrayProperty(scopeRecord, 'resources');
  if (resources.length > 0 && hasResourceArrayScope(resources, required)) {
    return true;
  }

  const modules = getStringArrayProperty(scopeRecord, 'modules');
  const mappedModule = MODULE_MAP[String(required)];
  if (mappedModule && modules.includes(mappedModule)) {
    return true;
  }

  const scopeRoot = normalizeScopeRoot(scopeRecord);
  return hasObjectScope(scopeRoot, required);
};

const getScopeResources = (scopes: PartnerScopePayload) => {
  if (!scopes || typeof scopes !== 'object') {
    return [] as string[];
  }
  return getStringArrayProperty(scopes as Record<string, unknown>, 'resources');
};

const SCOPE_LABELS = PARTNER_SCOPE_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

interface PartnerUserRow {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: Date | string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizePartnerUserRow = (value: unknown): PartnerUserRow | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    email: typeof value.email === 'string' ? value.email : '',
    name: typeof value.name === 'string' ? value.name : '',
    role: typeof value.role === 'string' ? value.role : '',
    isActive: typeof value.isActive === 'boolean' ? value.isActive : false,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : '',
  };
};

export function PartnerDetailPage() {
  const { message } = App.useApp();
  const { id } = useParams();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [form] = Form.useForm();
  const utils = trpc.useUtils();

  const { data: partnerCompany, isLoading: companyLoading } = trpc.partnerAdmin.getCompany.useQuery(
    { partnerCompanyId: id ?? '' },
    { enabled: !!id }
  );

  const { data: userData, isLoading: usersLoading } = trpc.partnerAdmin.listUsers.useQuery(
    { partnerCompanyId: id ?? '', page: 1, limit: 100 },
    { enabled: !!id }
  );

  const updateMutation = trpc.partnerAdmin.updateCompany.useMutation({
    onSuccess: () => {
      message.success('パートナー会社を更新しました');
      utils.partnerAdmin.getCompany.invalidate({ partnerCompanyId: id ?? '' });
      utils.partnerAdmin.listCompanies.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '更新に失敗しました');
    },
  });

  const updateScopesMutation = trpc.partnerAdmin.updateCompany.useMutation({
    onSuccess: () => {
      message.success('権限を更新しました');
      utils.partnerAdmin.getCompany.invalidate({ partnerCompanyId: id ?? '' });
    },
    onError: (error) => {
      message.error(error.message || '権限の更新に失敗しました');
    },
  });

  const updateUserMutation = trpc.partnerAdmin.updateUser.useMutation({
    onSuccess: () => {
      utils.partnerAdmin.listUsers.invalidate({ partnerCompanyId: id ?? '' });
    },
    onError: (error) => {
      message.error(error.message || '更新に失敗しました');
    },
  });

  useEffect(() => {
    if (partnerCompany) {
      form.setFieldsValue(partnerCompany);
    }
  }, [partnerCompany, form]);

  useBreadcrumbTitle('partner', partnerCompany?.name ?? null);

  const accessScopes = isRecord(partnerCompany?.accessScopes) ? partnerCompany.accessScopes : null;

  useEffect(() => {
    if (accessScopes) {
      setSelectedScopes(resolveSelectedScopes(accessScopes));
    }
  }, [accessScopes]);

  const users = useMemo(
    () => (userData?.users ?? []).flatMap((user) => {
      const normalized = normalizePartnerUserRow(user);
      return normalized ? [normalized] : [];
    }),
    [userData?.users]
  );

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      updateMutation.mutate({
        partnerCompanyId: id ?? '',
        ...values,
      });
    });
  };

  const resolveSelectedScopes = (scopes: PartnerScopePayload) => {
    return PARTNER_SCOPE_OPTIONS
      .filter((option) => hasPartnerScope(scopes, option.value))
      .map((option) => option.value);
  };

  const handleScopeSave = () => {
    if (!id) {
      message.error('パートナーIDが見つかりません');
      return;
    }
    updateScopesMutation.mutate({
      partnerCompanyId: id,
      accessScopes: { resources: selectedScopes },
    });
  };

  const renderScopeTags = (scopes: PartnerScopePayload) => {
    const resources = getScopeResources(scopes);
    const values = resources.length > 0
      ? resources
      : PARTNER_SCOPE_OPTIONS
          .filter((option) => hasPartnerScope(scopes, option.value))
          .map((option) => option.value);

    if (values.length === 0) {
      return <Text type="secondary">未設定</Text>;
    }

    return (
      <Space size={[4, 4]} wrap>
        {values.map((value) => (
          <Tag key={value}>{SCOPE_LABELS[value] ?? value}</Tag>
        ))}
      </Space>
    );
  };

  const columns: ResponsiveColumnType<PartnerUserRow>[] = [
    {
      title: 'ユーザー',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.email}</div>
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'ロール',
      dataIndex: 'role',
      key: 'role',
      render: (role) => role ?? '-',
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '状態',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '有効' : '招待中'}
        </Tag>
      ),
      mobileRole: 'tag',
      mobilePriority: 3,
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() =>
            updateUserMutation.mutate({
              partnerUserId: record.id,
              isActive: !record.isActive,
            })
          }
        >
          {record.isActive ? '無効化' : '再有効化'}
        </Button>
      ),
      mobileRole: 'hidden',
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBottom: 0 }}>パートナー詳細</Title>
        <Text type="secondary">{id}</Text>
      </div>

      <Card loading={companyLoading} title="会社情報">
        <Form form={form} layout="vertical" initialValues={partnerCompany} onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="会社名"
            rules={[{ required: true, message: '会社名を入力してください' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="status" label="ステータス">
            <Select options={statusOptions} />
          </Form.Item>
          <Form.Item name="billingModel" label="課金モデル">
            <Select options={billingModelOptions} />
          </Form.Item>
          <Form.Item name="clientType" label="対象クライアント">
            <Select options={clientTypeOptions} />
          </Form.Item>
          <Form.Item name="billingContactEmail" label="請求連絡先メール">
            <Input />
          </Form.Item>
          <Form.Item name="supportEmail" label="サポートメール">
            <Input />
          </Form.Item>
          <Button type="primary" onClick={handleSubmit} loading={updateMutation.isPending}>
            更新
          </Button>
        </Form>
      </Card>

      <Card
        title="パートナーユーザー"
        extra={
          <Button type="primary" onClick={() => setInviteOpen(true)}>
            招待
          </Button>
        }
      >
        <ResponsiveTable
          dataSource={users}
          columns={columns}
          loading={usersLoading}
          rowKey="id"
          pagination={false}
          mobileCardConfig={{ titleField: 'name' }}
        />
      </Card>

      <Card
        title="権限設定"
        extra={(
          <Button type="primary" onClick={handleScopeSave} loading={updateScopesMutation.isPending}>
            権限を保存
          </Button>
        )}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="info" message="この権限セットは、担当会社すべてに適用されます。" />
          <Checkbox.Group
            options={PARTNER_SCOPE_OPTIONS.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            value={selectedScopes}
            onChange={(values) => setSelectedScopes(values as string[])}
          />
          <div>
            <Text type="secondary">選択中の権限</Text>
            <div style={{ marginTop: 8 }}>
              {renderScopeTags({ resources: selectedScopes })}
            </div>
          </div>
        </Space>
      </Card>

      {id && (
        <InvitePartnerUserModal
          open={inviteOpen}
          partnerCompanyId={id}
          onCancel={() => setInviteOpen(false)}
          onSuccess={() => {
            setInviteOpen(false);
            utils.partnerAdmin.listUsers.invalidate({ partnerCompanyId: id });
          }}
        />
      )}

    </Space>
  );
}
